import { useEffect, useRef } from 'react'

import { AppProps } from '@/App'
import AppLayoutFooter from '@/components/AppLayout/AppLayoutFooter'
import AppLayoutProvider from '@/components/AppLayout/AppLayoutProvider'
import Ending from '@/components/Ending/Ending'
import Idle from '@/components/Idle/Idle'
import Interlude from '@/components/Interlude/Interlude'
import IntroEnterSwirl from '@/components/Interlude/IntroEnterSwirl'
import Loading from '@/components/Loading/Loading'
import LoadingCatch from '@/components/Loading/LoadingCatch'
import Questions from '@/components/Question/Questions'
import Runner from '@/components/Runner/Runner'
import AudioEngineSubtitles from '@/components/Subtitles/AudioEngineSubtitles'
import Summary from '@/components/Summary/Summary'
import { useAppState } from '@/composables/useAppState'
import useAudioMusic from '@/composables/useAudioMusic'
import { Audience, LTD, Persona } from '@/state/AppConfig'

import Decoration from '../Decoration/Decoration'
import Scenario from '../Scenario/Scenario'

export default function AppLayout(params: AppProps) {
  const { state, actor } = useAppState()
  const rootRef = useRef<HTMLDivElement>(null)
  const footerCenterRef = useRef<HTMLDivElement>(null)
  const footerRightRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const locale = params.locale || 'en'
    const backUrl = params.backUrl || null
    const persona = (params.persona as Persona) || null
    const relationship = params.relationship as Audience
    const interestedBy = params.interestedBy as LTD
    const userId = params.userId || 'anon-user'
    actor.send({ type: 'INITIALIZE', locale, userId, backUrl, persona, relationship, interestedBy })
  }, [params, actor])

  const music = useAudioMusic()
  const running = state.matches('running')
  const summaryOrEnding = state.matches('summary.intro') || state.matches('summary.content') || state.matches('ending')
  const close = state.matches('close')
  const { scenarioUnitName } = useAppState()

  useEffect(() => {
    if (!running) {
      music.play('TYCE_MUSIC_pages')
    }
  }, [music, running])

  useEffect(() => {
    if (close) {
      rootRef.current.dispatchEvent(new Event('simulation:close', { bubbles: true, composed: true }))
    }
  }, [close])

  useEffect(() => {
    if (rootRef.current) {
      if (running || summaryOrEnding) {
        rootRef.current.dispatchEvent(new Event('simulation:hide_header', { bubbles: true, composed: true }))
      } else {
        rootRef.current.dispatchEvent(new Event('simulation:show_header', { bubbles: true, composed: true }))
      }
    }
  }, [running, summaryOrEnding, rootRef])

  useEffect(() => {
    const event = new CustomEvent('simulation:change_scenario', {
      detail: scenarioUnitName,
      bubbles: true,
      composed: true,
    })
    if (rootRef.current) {
      rootRef.current.dispatchEvent(event)
    }
  }, [scenarioUnitName, rootRef])

  return (
    <AppLayoutProvider
      value={{
        root: rootRef,
        footerCenter: footerCenterRef,
        footerRight: footerRightRef,
      }}
    >
      <main
        ref={rootRef}
        className={`text-body relative self-start top-0 left-0 w-full min-h-dvh md:min-h-screen overflow-hidden z-0 grid bg-tertiary text-primary`}
      >
        <AppLayoutFooter />
        <AudioEngineSubtitles />

        {state.matches('idle') && !params.persona && <Idle {...params} />}

        <LoadingCatch
          className='col-span-1 col-start-1 row-span-1 row-start-1'
          isVisible={state.matches('loading.catch')}
        />
        <Loading
          className='col-span-1 col-start-1 row-span-1 row-start-1'
          isVisible={state.matches('loading.proceed')}
        />
        <Interlude
          className='col-span-1 col-start-1 row-span-1 row-start-1'
          name='intro'
          isVisible={state.matches('intro')}
        />

        <Questions />

        <Decoration show={state.matches('scenario')} />

        {state.matches('scenario') && <Scenario />}
        {state.matches('running') && <Runner />}
        {state.matches('ending') && <Ending />}

        <Interlude
          className='col-span-1 col-start-1 row-span-1 row-start-1'
          name='outro'
          isVisible={state.matches('outro')}
        />
        {state.matches('outro') && <IntroEnterSwirl className='col-span-1 col-start-1 row-span-1 row-start-1' />}

        {state.matches('summary') && <Summary />}
      </main>
    </AppLayoutProvider>
  )
}
